import {Actions, createEffect, ofType} from "@ngrx/effects";
import {catchError, exhaustMap, map, of} from "rxjs";
import {inject, Injectable} from "@angular/core";
import {appUserActions} from "@core/ngrx/actions"
import {UsersService} from "@core/service/aux/users.service";
import {RoleModel} from "@core/model/aux/role.model";
import {PaginatedResultModel} from "@core/model/paginated-result.model";
import {PermissionModel} from "@core/model/aux/permission.model";
import {AppUserModel} from "@core/model/aux/app-user.model";
import _ from "lodash";

@Injectable()
export class UserEffects {
  actions$: Actions = inject(Actions)
  usersService: UsersService = inject(UsersService);

  loadRoles = createEffect(() =>
    this.actions$.pipe(
      ofType(appUserActions.loadRoles),
      exhaustMap(action => this.usersService.getUserRoles(action.params).pipe(
        map(response => {
          return appUserActions.loadRolesSuccess({
            response: response as PaginatedResultModel<RoleModel>,
          });
        }),
        catchError((error: any) => of(appUserActions.loadRolesError({msg: "error"})))
      ))
    )
  );

  loadUsers = createEffect(() =>
    this.actions$.pipe(
      ofType(appUserActions.loadUsers),
      exhaustMap(action => this.usersService.getUsers().pipe(
        map(response => {
          return appUserActions.loadUsersSuccess({
            users: _.sortBy((response as AppUserModel[]), 'name'),
          });
        }),
        catchError((error: any) => of(appUserActions.loadUsersError({msg: "error"})))
      ))
    )
  );

  loadPermissions = createEffect(() =>
    this.actions$.pipe(
      ofType(appUserActions.loadPermissions),
      exhaustMap(action => this.usersService.getPermissions(action.params).pipe(
        map(response => {
          return appUserActions.loadPermissionsSuccess({
            permissions: response.data as PermissionModel[],
          });
        }),
        catchError((error: any) => of(appUserActions.loadPermissionsError({msg: "error"})))
      ))
    )
  );

  loadRoleDetails = createEffect(() =>
    this.actions$.pipe(
      ofType(appUserActions.loadRoleDetails),
      exhaustMap(action => this.usersService.getRoleDetails(action.role).pipe(
        map(response => {
          return appUserActions.loadRoleDetailsSuccess({
            role: response as RoleModel,
          });
        }),
        catchError((error: any) => of(appUserActions.loadRoleDetailsError({msg: "error"})))
      ))
    )
  );

  saveRole$ = createEffect(() =>
    this.actions$.pipe(
      ofType(appUserActions.saveRole),
      exhaustMap(action => this.usersService.saveRole(action.role).pipe(
        map(response => {
          return appUserActions.saveRoleSuccess({role: response});
        }),
        catchError((error: any) => {
          return of(appUserActions.saveRoleError({msg: error.error}));
        })
      ))
    ));

  updateRole$ = createEffect(() =>
    this.actions$.pipe(
      ofType(appUserActions.updateRole),
      exhaustMap(action => this.usersService.updateRole(action.role).pipe(
        map(response => {
          return appUserActions.updateRoleSuccess({role: response});
        }),
        catchError((error: any) => {
          return of(appUserActions.updateRoleError({msg: error.error}));
        })
      ))
    ));

  deleteRole$ = createEffect(() =>
    this.actions$.pipe(
      ofType(appUserActions.deleteRole),
      exhaustMap(action => this.usersService.deleteRole(action.role).pipe(
        map(response => {
          return appUserActions.deleteRoleSuccess();
        }),
        catchError((error: any) => {
          return of(appUserActions.deleteRoleError({msg: error.error}));
        })
      ))
    ));

  assignRoleToUser = createEffect(() =>
    this.actions$.pipe(
      ofType(appUserActions.assignRoleToUser),
      exhaustMap(action => this.usersService.assignRoleToUser(action.user).pipe(
        map(response => {
          return appUserActions.assignRoleToUserSuccess({user: response[0]});
        }),
        catchError((error: any) => {
          return of(appUserActions.assignRoleToUserError({msg: error.error.message}));
        })
      ))
    ));

}
